import useTranslation from 'next-translate/useTranslation';
import { Children, useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { ROW_TO_DISPLAY, Translate } from '@core/constant';
import { EFontWeight, EIcon, EPosition, ESize, ETypographyVariant } from '@core/type';

import { Icon } from '../icon';
import { Box } from '../layout';
import { Typography } from '../typography';
import { AccordionWithHiddenItemsProps } from './interface-accordion';

const StyledChildrenContainer = styled(Box)<{ isOpen: boolean }>(({ isOpen }) => {
  return css`
    display: grid;
    transition: grid-template-rows 0.35s ease;
    grid-template-rows: ${isOpen ? '1fr' : '0fr'};
  `;
});

const StyledHeightContainer = styled(Box)<{
  isOpen: boolean;
  isOverflowing: boolean;
  isMeasured: boolean;
  closedHeight: number;
  openHeight: number;
}>`
  transition: max-height 0.35s ease;
  overflow: hidden;
  max-height: ${({ isMeasured, isOverflowing, isOpen, closedHeight, openHeight }) => {
    if (!isMeasured) {
      return 'auto';
    }

    if (isOverflowing) {
      return isOpen ? `${openHeight}px` : `${closedHeight}px`;
    }
    return 'auto';
  }};
`;

export const AccordionWithHiddenItems = ({
  children,
  itemsCount,
  itemsToShow,
}: AccordionWithHiddenItemsProps) => {
  const { t: tButton } = useTranslation(Translate.common.BUTTON);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isSlicingByCount = itemsCount !== undefined && itemsToShow !== undefined;

  const items = Children.toArray(children);

  // Logic if the items are not sliced by count
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const [closedHeight, setClosedHeight] = useState<number>(0);
  const [openHeight, setOpenHeight] = useState<number>(0);
  const [isMeasured, setIsMeasured] = useState<boolean>(false);

  const shouldShowToggle = isSlicingByCount ? itemsCount > itemsToShow : isOverflowing;

  useEffect(() => {
    if (!isSlicingByCount && containerRef.current) {
      const firstChild = containerRef.current.firstElementChild;
      if (firstChild) {
        const tagHeight = firstChild.clientHeight;
        const threshold = tagHeight * ROW_TO_DISPLAY;
        const totalHeight = containerRef.current.scrollHeight;
        if (totalHeight > threshold) {
          setIsOverflowing(true);
          setClosedHeight(threshold);
          setOpenHeight(totalHeight);
        } else {
          setIsOverflowing(false);
        }
      } else {
        setIsOverflowing(false);
      }
      setIsMeasured(true);
    }
  }, [children, isSlicingByCount]);

  const toggleButton = (
    <Box
      cursor="pointer"
      display="inline-flex"
      marginTop="6px"
      marginLeft="-4px"
      onClick={() => setIsOpen((prev) => !prev)}
    >
      <Icon
        type={EIcon.ANGLE}
        size={ESize.MD}
        marginRight="8px"
        orientation={isOpen ? EPosition.TOP : EPosition.BOTTOM}
      />
      <Typography
        variant={ETypographyVariant.BODY3}
        fontWeight={EFontWeight.MEDIUM}
        lineHeight="24px"
        textTransform="uppercase"
      >
        {isOpen ? tButton('viewLess.title') : tButton('viewMore.title')}
      </Typography>
    </Box>
  );

  return (
    <>
      {isSlicingByCount ? (
        <>
          {items.slice(0, itemsToShow)}
          {items.slice(itemsToShow).length > 0 && (
            <StyledChildrenContainer isOpen={isOpen}>
              <Box overflow="hidden">{items.slice(itemsToShow)}</Box>
            </StyledChildrenContainer>
          )}
        </>
      ) : (
        <StyledHeightContainer
          ref={containerRef}
          isOverflowing={isOverflowing}
          isOpen={isOpen}
          isMeasured={isMeasured}
          closedHeight={closedHeight}
          openHeight={openHeight}
        >
          {children}
        </StyledHeightContainer>
      )}
      {shouldShowToggle && toggleButton}
    </>
  );
};
